<template>
    <div>       
        <!-- Add Contact Modal -->
        <b-modal
            v-model="createModal"
            :title="modalTitle"
            @hidden="onCreateModalHidden"
            centered
            size="md"
            :hide-footer="showCreateSuccess"
            content-class="bg-gray-50"
        >
            <div class="d-flex" style="min-height: 300px">
                <transition name="fade-fast" mode="out-in">
                    <save-success v-if="showCreateSuccess" :message="skipRedirectOnCreate ? createSuccessMessage : createSuccessMessageRedirect"></save-success>
                    <contact-detail-form
                        v-else
                        ref="createForm"
                        :new-data="newData"
                        :hide-company-input="hideCompanyInput"
                        :company-data="companyData"
                        :allow-company-change="allowCompanyChange"
                        @save-complete="handleCreateComplete"
                        create-new
                        no-refresh-on-update
                        :email-required="emailRequired"
                        edit-mode
                        class="w-100"
                    ></contact-detail-form>
                </transition>
            </div>
            <template v-slot:modal-footer="{ cancel }">
                <b-button 
                    variant="minimal-secondary" 
                    @click="cancel()"
                >
                    Cancel
                </b-button>
                <b-button 
                    v-if="!hideSaveClose" 
                    variant="primary" 
                    class="save-close-button"
                    @click="createItemForm({skipRedirect: true})"
                >
                    <span v-html="saveCloseHtml"></span>
                </b-button>
                <b-button 
                    v-if="!hideSaveRedirect" 
                    variant="primary" 
                    class="save-redirect-button"
                    @click="createItemForm({skipRedirect: false})"
                >
                    <span v-html="saveRedirectHtml"></span>
                </b-button>
            </template>
        </b-modal>

        <!-- UPDATE MODAL -->
        <b-modal
            v-model="updateModal"
            title="Contact Details"
            @hidden="onUpdateModalHidden"
            centered
            size="md"
            :hide-footer="showUpdateSuccess"
            content-class="bg-gray-50"
        >
            <div class="d-flex" style="min-height: 300px">
                <transition name="fade-fast" mode="out-in">
                    <save-success v-if="showUpdateSuccess" :message="updateSuccessMessage"></save-success>
                    <contact-detail-form
                        v-else
                        :tu-id="updateItemId"
                        ref="updateForm"
                        @update-complete="handleUpdateComplete"
                        @access-updated="handleInviteUpdate"
                        no-refresh-on-update
                        :email-required="emailRequired"
                        class="w-100"
                    ></contact-detail-form>
                </transition>
            </div>
            <template v-slot:modal-footer="{ cancel }">
                <b-button v-if="editMode" variant="outline-secondary" @click="cancelEditMode">Cancel Edits</b-button>
                <b-button v-else variant="outline-secondary" @click="cancel()">Close</b-button>
                <b-button v-if="editMode" variant="success" @click="updateItemForm">Save Edits</b-button>
                <b-button v-else variant="secondary" @click="enterEditMode">Edit</b-button>
            </template>
        </b-modal>

    </div>
</template>

<script>
// modal to add / update Contact basic data - holds ContactDetailForm

import ContactDetailForm from './ContactDetailForm';
import SaveSuccess from './functional-comps/SaveSuccess';

export default {
    name: 'ContactCreateUpdateModal',
    components: {
        ContactDetailForm,
        SaveSuccess,
    },
    props: {
        redirectAfterCreate: {
            // NO LONGER BEING USED -- replaced by having 2 separate buttons that control skipRedirectOnCreate
            type: Boolean,
            default: false,
        },
        modalTitle: {
            type: String,
            default: 'Add Contact',
        },
        newData: {  // passed directly to newData prop on ContactDetailForm (Create version only, not update)
            // should be object like this:  {company: companyId}
            type: Object,
            required: false,
        },
        hideCompanyInput: {  // set to true if passing in companyId with newData, only applies to Create version
            type: Boolean,
            default: false,
        },
        companyData: {  // passed directly to companyData prop on ContactDetailForm (only relevant for create, not update)
            // should be full company object, with ID and name at least
            // if no preset company, then it is OK to just pass an empty object (should work)
            type: Object,
            required: false,
        },
        allowCompanyChange: {
            // IF companyData is set / passed in, but you still want to allow the company to be changed from that preset value, set this to True - only applies to add/create
            type: Boolean,
            default: false,
        },
        successMessageTime: {  // set to 0 to skip showing success message
            type: Number,
            default: 2000,
        },
        // the text (as HTML) and visibility of the multiple save buttons
        saveCloseHtml: {
            type: String,
            default: 'Save &amp; close',
        },
        hideSaveClose: { // should we hide the 'save and close' button?
            type: Boolean,
            default: false,
        },
        saveRedirectHtml: {
            type: String,
            default: 'Save &amp; continue editing &rarr;',
        },
        hideSaveRedirect: {  // should we hide the 'save and redirect' button?
            type: Boolean,
            default: false,
        },
        emailRequired: {  // is email field required?  some places we need it, some we don't - passed directly to ContactDetailForm
            type: Boolean,
            default: false,
        }
    },
    data: function() {
        return {
            createModal: false,
            updateModal: false,
            editMode: false,
            updateItemId: null,
            showUpdateSuccess: false,  
            showCreateSuccess: false,
            updateSuccessMessage: "Updated successfully",
            createSuccessMessage: "Contact saved",
            createSuccessMessageRedirect: "Contact saved.  Let's add some details...",
            //successMessageTime: 2000,
            skipRedirectOnCreate: false,
        }
    },
    computed: {

    },
    methods: {
        // below are 3 main trigger functions -- can call these publicly
        startUpdate: function(updateId) {
            this.updateItemId = updateId;
            this.updateModal = true;
            // this.enterEditMode();  // does not work yet... ref doesn't exist
            // DO NOT USE startUpdate yet... (its just the same as startView) -- loading order matters here
        },
        startView: function(itemId) {
            // this is what startUpdate used to do, but for update we now added that it goes straight to editMode = true
            this.updateItemId = itemId;
            this.updateModal = true;  // but does not go directly to edit mode
        },
        startCreate: function() {
            this.createModal = true;
        },
        handleUpdateComplete: function(updatedObject) {
            // updatedObject MAY be the newly updated object returned from API (in response.data) - depends on your endpoint
            console.log('Updated response data below');
            console.log(updatedObject);
            this.showUpdateSuccess = true;
            this.$emit('update-complete', updatedObject);  // relay the update-complete event from form
            setTimeout(() => {
                // should we redirect to detail page after update?  if so, put here
                this.updateModal = false;
                this.$emit('update-modal-hide');  // tell parent we're closing
            }, this.successMessageTime);
        },
        onUpdateModalHidden: function() {
            this.editMode = false;
            this.showUpdateSuccess = false;
            this.$emit('update-modal-hidden');  // tell the parent we're hidden
        },
        handleCreateComplete: function(newObject) {
            // newObject should be the newly created object returned from API (in response.data)
            console.log('From parent component, we got this newObject passed up from save-complete event');
            console.log(newObject);
            this.$emit('create-complete', newObject);  // relay the save-complete event from form
            if (this.successMessageTime) this.showCreateSuccess = true;
            setTimeout(() => {  // after the timeout, do the redirect if we have the URL
                let redirectUrl = newObject.company_url;
                if (redirectUrl && !this.skipRedirectOnCreate) {
                    window.location.href = redirectUrl;  // THE REDIRECT
                } else {  // if we didn't have redirect URL just hide the modal and emit event to tell parent we're done
                    this.createModal = false;
                    this.$emit('create-modal-hide');  // tell parent we're closing modal
                }
            }, this.successMessageTime);
        },
        onCreateModalHidden: function() {
            this.editMode = false;  // just in case?
            this.showCreateSuccess = false;
            this.skipRedirectOnCreate = false;  // reset this
            this.$emit('create-modal-hidden');  // tell the parent we're hidden
        },
        handleInviteUpdate: function(updatedTuObj) {
            // called when we update the TU's portal invite status, like when sending invite renewal or reminder email
            // just emit something to update a list on parent, pass up the object you got
            this.$emit('update-complete', updatedTuObj);  // same event that you use when updating an object... its the same really
        },
        // NEXT 4 METHODS CALL METHOD ON CHILD FORM COMPONENT
        enterEditMode: function() {
            // set local edit mode and call cancelEditMode on child form
            this.editMode = true;
            this.$refs.updateForm.enterEditMode();  // ref might not exist yet!
        },
        cancelEditMode: function() {
            // set local edit mode and call cancelEditMode on child form
            this.editMode = false;
            this.$refs.updateForm.cancelEditMode();
        },
        createItemForm: function(params) {
            const skipRedirect = !!params.skipRedirect;  // pass in {skipRedirect: true} to skip the redirect after save is complete
            this.skipRedirectOnCreate = skipRedirect;
            this.$refs.createForm.saveNewItem();
        },
        updateItemForm: function() {
            // call update method on child form
            // no need to set local editMode value here... let events from child component set that
            // but make sure you watch for them with @edit-on and @edit-off
            this.$refs.updateForm.updateItemData();
        },
    }
}
</script>

<style>

</style>